// App state as signals (api.ts AppState).
//
//   Store    every writable signal, owned by the services layer
//   AppState the same signals read-only, plus `snapshot` and `lensResult` derived from
//            (leader, date, lens) through the world
//
// The UI only ever gets the read-only view; actions.ts is the sole writer.

import { computed, signal, type Signal } from '@preact/signals';
import type { DateStr, LeaderId } from '../core/schema.ts';
import type { LensResult, WorldSnapshot } from '../world/api.ts';
import type { ViewMode } from '../view/api.ts';
import type { AppState, HoverInfo, Notification, PanelId } from './api.ts';

export interface CameraRequest {
  seq: number;
  hex: number;
  zoom?: number;
}

export interface Loading {
  done: number;
  total: number;
  label: string;
}

export interface Store {
  leader: Signal<LeaderId | null>;
  date: Signal<DateStr>;
  asOf: Signal<DateStr>;
  lens: Signal<string | null>;
  mode: Signal<ViewMode>;
  selection: Signal<{ hex: number; iso3: string | null } | null>;
  hover: Signal<HoverInfo | null>;
  panel: Signal<PanelId>;
  panelArg: Signal<string | null>;
  notifications: Signal<Notification[]>;
  log: Signal<Notification[]>;
  playing: Signal<boolean>;
  speed: Signal<number>;
  camera: Signal<CameraRequest | null>;
  loading: Signal<Loading | null>;
  /** Bumps when world data lands (an events year), which changes what a snapshot derives. */
  dataVersion: Signal<number>;
}

/** Turns per second at the start; the UI offers the steps. */
export const DEFAULT_SPEED = 1;

export function createStore(asOf: DateStr): Store {
  return {
    leader: signal(null),
    date: signal(asOf),
    asOf: signal(asOf),
    lens: signal(null),
    mode: signal('3d'),
    selection: signal(null),
    hover: signal(null),
    panel: signal('none'),
    panelArg: signal(null),
    notifications: signal([]),
    log: signal([]),
    playing: signal(false),
    speed: signal(DEFAULT_SPEED),
    camera: signal(null),
    loading: signal(null),
    dataVersion: signal(0),
  };
}

/** What the state derives from the world: enough for tests to fake. */
export interface Deriver {
  snapshot(date: DateStr, leader: LeaderId): WorldSnapshot;
  lens(id: string, date: DateStr, leader: LeaderId): LensResult;
}

/** The read-only AppState over a store; `snapshot` and `lensResult` are null without a leader (or a world). */
export function appState(store: Store, world: Deriver | null): AppState {
  // The world keys its caches on what it has loaded (fog of war), so reading `dataVersion`
  // makes both computeds re-derive when an events year lands.
  const snapshot = computed<WorldSnapshot | null>(() => {
    const leader = store.leader.value;
    store.dataVersion.value;
    return world && leader ? world.snapshot(store.date.value, leader) : null;
  });

  const lensResult = computed<LensResult | null>(() => {
    const leader = store.leader.value;
    const id = store.lens.value;
    store.dataVersion.value;
    return world && leader && id ? world.lens(id, store.date.value, leader) : null;
  });

  return { ...store, snapshot, lensResult };
}
